import { useEffect, useState } from "react";
import { Save, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type {
  ContentFrameworkColumn,
  ContentFrameworkPrinciple,
  ContentFrameworkTopic,
} from "@/lib/composerCore";
import { TopicChip } from "./TopicChip";
import { PrincipleSlot } from "./PrincipleSlot";
import { NewTopicRow } from "./NewTopicRow";

const PRINCIPLE_SLOTS = 2;

type Props = {
  workspaceId: string;
  column: ContentFrameworkColumn;
  allColumns: ContentFrameworkColumn[];
  principles: ContentFrameworkPrinciple[];
  topics: ContentFrameworkTopic[];
  allTopics: ContentFrameworkTopic[];
  usage: Map<string, number>;
  topicCap: number;
  showElaborations: boolean;
  onChange: () => void | Promise<void>;
};

export function FilledColumnCard({
  workspaceId,
  column,
  allColumns,
  principles,
  topics,
  allTopics,
  usage,
  topicCap,
  showElaborations,
  onChange,
}: Props) {
  const [label, setLabel] = useState(column.label);
  const [progressionLabel, setProgressionLabel] = useState(column.progression_key_label ?? "");
  const [progressionDescription, setProgressionDescription] = useState(column.progression_key_description ?? "");
  const [savingHeader, setSavingHeader] = useState(false);

  useEffect(() => {
    setLabel(column.label);
    setProgressionLabel(column.progression_key_label ?? "");
    setProgressionDescription(column.progression_key_description ?? "");
  }, [column.id, column.label, column.progression_key_label, column.progression_key_description]);

  const headerDirty =
    label !== column.label ||
    (progressionLabel || "") !== (column.progression_key_label ?? "") ||
    (progressionDescription || "") !== (column.progression_key_description ?? "");

  const saveHeader = async () => {
    const trimmed = label.trim();
    if (!trimmed) {
      toast.error("Column name can't be empty");
      return;
    }
    setSavingHeader(true);
    const { error } = await supabase
      .from("content_framework_columns")
      .update({
        label: trimmed,
        progression_key_label: progressionLabel.trim() || null,
        progression_key_description: progressionDescription.trim() || null,
      })
      .eq("id", column.id);
    setSavingHeader(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Column saved");
    await onChange();
  };

  const [columnDeleteOpen, setColumnDeleteOpen] = useState(false);
  const [moveTargetColumnId, setMoveTargetColumnId] = useState<string>("");
  const columnTopics = topics;
  const columnAssignmentTotal = columnTopics.reduce((acc, t) => acc + (usage.get(t.id) ?? 0), 0);
  const otherColumns = allColumns.filter((c) => c.id !== column.id);

  const performColumnDelete = async () => {
    const { error } = await supabase.from("content_framework_columns").delete().eq("id", column.id);
    if (error) {
      toast.error(error.message);
      return;
    }
    setColumnDeleteOpen(false);
    await onChange();
  };

  const performColumnMoveAndDelete = async () => {
    if (!moveTargetColumnId) {
      toast.error("Pick a destination column");
      return;
    }
    const ids = columnTopics.map((t) => t.id);
    if (ids.length) {
      const { error: moveErr } = await supabase
        .from("content_framework_topics")
        .update({ framework_column_id: moveTargetColumnId })
        .in("id", ids);
      if (moveErr) {
        toast.error(moveErr.message);
        return;
      }
    }
    await performColumnDelete();
    setMoveTargetColumnId("");
  };

  const removeColumn = async () => {
    if (columnTopics.length === 0 && principles.length === 0) {
      if (!confirm("Delete this column?")) return;
      await performColumnDelete();
      return;
    }
    setColumnDeleteOpen(true);
  };

  const principleSlots = Array.from({ length: PRINCIPLE_SLOTS }, (_, idx) => principles[idx] ?? null);

  return (
    <Card className="flex flex-col shadow-soft">
      <CardHeader className="space-y-3 pb-3">
        <div className="flex items-center justify-between gap-2">
          <Badge variant="outline">Column {column.position + 1}</Badge>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => void removeColumn()}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
        <Input
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          placeholder="Column name"
          className="font-medium"
        />
        <div className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground">Progression key</p>
          <Input
            value={progressionLabel}
            onChange={(event) => setProgressionLabel(event.target.value)}
            placeholder="Progression key label"
          />
          <Textarea
            value={progressionDescription}
            onChange={(event) => setProgressionDescription(event.target.value)}
            placeholder="Elaboration — what 'progress' means in this column for the agents"
            rows={2}
          />
        </div>
        {headerDirty ? (
          <Button size="sm" onClick={() => void saveHeader()} disabled={savingHeader} className="self-start">
            <Save className="mr-1 h-4 w-4" />
            Save changes
          </Button>
        ) : null}
      </CardHeader>

      <CardContent className="flex-1 space-y-4">
        <div className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground">Core principles (2)</p>
          {principleSlots.map((principle, idx) => (
            <PrincipleSlot
              key={principle?.id ?? `p-slot-${idx}`}
              workspaceId={workspaceId}
              column={column}
              position={idx}
              principle={principle}
              showElaborations={showElaborations}
              onChange={onChange}
            />
          ))}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground">Topics</p>
            <span className="text-xs text-muted-foreground">{topics.length}/{topicCap}</span>
          </div>
          <div className="space-y-2">
            {topics.map((topic) => (
              <TopicChip
                key={topic.id}
                topic={topic}
                usageCount={usage.get(topic.id) ?? 0}
                reassignTargets={allTopics.filter((t) => t.id !== topic.id)}
                showElaborations={showElaborations}
                onChange={onChange}
              />
            ))}
            {topics.length < topicCap ? (
              <NewTopicRow workspaceId={workspaceId} column={column} nextPosition={topics.length} onChange={onChange} />
            ) : null}
          </div>
        </div>
      </CardContent>

      <Dialog open={columnDeleteOpen} onOpenChange={setColumnDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete column "{column.label}"?</DialogTitle>
            <DialogDescription>
              This column has {columnTopics.length} topic{columnTopics.length === 1 ? "" : "s"}
              {columnAssignmentTotal > 0
                ? ` used on ${columnAssignmentTotal} excerpt assignment${columnAssignmentTotal === 1 ? "" : "s"}.`
                : " with no excerpt assignments."}
            </DialogDescription>
          </DialogHeader>
          {otherColumns.length > 0 && columnTopics.length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground">
                Move topics to
              </p>
              <Select value={moveTargetColumnId} onValueChange={setMoveTargetColumnId}>
                <SelectTrigger>
                  <SelectValue placeholder="Pick a destination column" />
                </SelectTrigger>
                <SelectContent>
                  {otherColumns.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="ghost" onClick={() => setColumnDeleteOpen(false)}>Cancel</Button>
            {otherColumns.length > 0 && columnTopics.length > 0 ? (
              <Button
                variant="secondary"
                onClick={() => void performColumnMoveAndDelete()}
                disabled={!moveTargetColumnId}
              >
                Move topics, then delete column
              </Button>
            ) : null}
            <Button
              variant="destructive"
              onClick={() => void performColumnDelete()}
            >
              Delete column and {columnTopics.length} topic{columnTopics.length === 1 ? "" : "s"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
